import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import {
  clearSessionPosition,
  loadSessionPosition,
  saveSessionPosition,
  type SavedSessionPosition,
  type SessionPositionInput,
} from '../session-position.storage';
import type { InitialDataCatalog, InitialDataMode } from '../types';
import type { useInitialDataSetup } from './use-initial-data-setup';
import type { useQuestionSession } from './use-question-session';

type Params = {
  mode: InitialDataMode;
  setup: ReturnType<typeof useInitialDataSetup>;
  catalog: InitialDataCatalog;
  session: ReturnType<typeof useQuestionSession>;
};

/**
 * Pause and resume for an initial-data session. The position is saved after
 * every submit, skip or move, when the page is hidden (closing the tab counts
 * as a pause) and on an explicit pause, then offered again on the start screen.
 */
export function useSessionPause({ mode, setup, catalog, session }: Params) {
  const { user } = useApp();
  const userId = user?.id ? String(user.id) : 'anonymous';

  const [saved, setSaved] = useState<SavedSessionPosition | null>(null);
  const [resumeTarget, setResumeTarget] = useState<SavedSessionPosition | null>(null);
  // Written answers are not deduplicated against past contributions, so the
  // count sent before the pause is carried over by hand.
  const [answeredBefore, setAnsweredBefore] = useState(0);

  // Last thing persisted, so hiding the page right after a submit does not send it twice.
  const lastPersisted = useRef('');

  useEffect(() => {
    let cancelled = false;
    void loadSessionPosition(userId, mode).then((position) => {
      // A session started meanwhile already replaced it.
      if (!cancelled && !lastPersisted.current) setSaved(position);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, mode]);

  const isResuming = resumeTarget !== null;
  const { restore } = session;

  // Once the questions of the saved sector and language are in, put the session back where it was.
  useEffect(() => {
    if (!resumeTarget) return;
    if (setup.sectorId !== resumeTarget.sectorId || setup.language !== resumeTarget.language) return;
    if (catalog.isLoadingQuestions) return;
    if (!catalog.questionsError) restore(resumeTarget.currentQuestionId, resumeTarget.skippedQuestionIds);
    setAnsweredBefore(mode === 'text' ? resumeTarget.answeredCount ?? 0 : 0);
    setResumeTarget(null);
  }, [resumeTarget, setup.sectorId, setup.language, catalog.isLoadingQuestions, catalog.questionsError, restore, mode]);

  const inSession = setup.step === 'session' && !isResuming && !catalog.isLoadingQuestions && catalog.questions.length > 0;

  const { isComplete, current, skipped, submittedThisSession, tally } = session;

  const snapshot = useMemo<SessionPositionInput | null>(() => {
    if (!inSession) return null;
    return {
      language: setup.language,
      sectorId: setup.sectorId,
      currentQuestionId: isComplete ? null : current?.id ?? null,
      skippedQuestionIds: [...skipped],
      answeredCount: mode === 'text' ? answeredBefore + submittedThisSession : tally.submitted,
      totalCount: tally.total,
    };
  }, [inSession, setup.language, setup.sectorId, isComplete, current, skipped, submittedThisSession, tally, mode, answeredBefore]);

  const nothingLeft = isComplete && tally.remaining === 0 && tally.skipped === 0;

  const persist = useCallback(
    (input: SessionPositionInput | null, finished: boolean) => {
      if (!input) return;
      const key = JSON.stringify([userId, mode, finished, input]);
      if (key === lastPersisted.current) return;
      lastPersisted.current = key;
      if (finished) {
        clearSessionPosition(userId, mode);
        setSaved(null);
      } else {
        setSaved(saveSessionPosition(userId, mode, input));
      }
    },
    [userId, mode],
  );

  // Every submit, skip or move changes the snapshot: save it.
  useEffect(() => {
    persist(snapshot, nothingLeft);
  }, [snapshot, nothingLeft, persist]);

  const latest = useRef({ snapshot, nothingLeft, persist });
  latest.current = { snapshot, nothingLeft, persist };

  // Hiding the page (tab switch, tab closed, phone locked) counts as a pause.
  useEffect(() => {
    const save = () => {
      const current = latest.current;
      current.persist(current.snapshot, current.nothingLeft);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') save();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', save);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', save);
    };
  }, []);

  /** Saves where the contributor is and goes back to the start screen. */
  const pause = () => {
    persist(snapshot, nothingLeft);
    setup.setStep('setup');
  };

  const resume = () => {
    if (!saved) return;
    setup.setLanguage(saved.language);
    setup.setSectorId(saved.sectorId);
    setResumeTarget(saved);
    setup.setStep('session');
  };

  /** Forgets the saved position; the contributor picks a language and sector again. */
  const restart = () => {
    lastPersisted.current = '';
    clearSessionPosition(userId, mode);
    setSaved(null);
  };

  /** A fresh session does not carry the count of a resumed one. */
  const resetCarryOver = () => setAnsweredBefore(0);

  return { saved, isResuming, pause, resume, restart, resetCarryOver };
}
